import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "./RichTextEditor";
import { CheckCircle2, Loader2, Smile } from "lucide-react";
import { toast } from "sonner";
import { TemplateVarsHint } from "./TemplateVarsHint";


export const PostTripTemplateCard = ({ api, axiosConfig }) => {
  const [tpl, setTpl] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${api}/settings/post-trip-template`, axiosConfig)
      .then((r) => {
        setTpl(r.data.template);
        setDefaults(r.data.defaults);
      })
      .catch(() => toast.error("Error al cargar la plantilla post viaje"));
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => setTpl((t) => ({ ...t, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${api}/settings/post-trip-template`, { template: tpl }, axiosConfig);
      toast.success("Plantilla post viaje guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!tpl) return null;

  return (
    <Card className="glass-card border-0 mt-10" data-testid="post-trip-template-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-lg font-heading text-sky-900 flex items-center gap-2">
            <Smile className="w-5 h-5 text-amber-500" />
            Post viaje · Bienvenido de vuelta
          </CardTitle>
          <button type="button" onClick={() => setTpl(defaults)} className="text-xs text-sky-600 hover:text-sky-800 hover:underline" data-testid="post-trip-template-reset">
            Restaurar original
          </button>
        </div>
        <CardDescription>
          Se envía por email (y WhatsApp si hay teléfono) a las horas configuradas después del regreso.
        </CardDescription>
        <TemplateVarsHint compact />
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-sky-900 font-medium">Asunto</Label>
          <Input value={tpl.subject} onChange={set("subject")} className="border-sky-200" data-testid="post-trip-template-subject" />
        </div>
        <div>
          <Label className="text-sky-900 font-medium">Mensaje</Label>
          <RichTextEditor value={tpl.message} onChange={(v) => setTpl((t) => ({ ...t, message: v }))} axiosConfig={axiosConfig} previewKind="post_trip" testId="post-trip-template-editor" />
        </div>
        <div>
          <Label className="text-sky-900 font-medium">Link base de la encuesta</Label>
          <Input value={tpl.survey_url} onChange={set("survey_url")} className="border-sky-200 font-mono text-xs" data-testid="post-trip-template-survey-url" />
          <p className="text-xs text-sky-600/70 mt-1">
            Se agrega <code>?n=</code> con nombre y apellido codificados. Ej: <code>?n=Simon%20Fiocco</code>
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="btn-sky text-white" data-testid="post-trip-template-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Guardar plantilla post viaje
        </Button>
      </CardContent>
    </Card>
  );
};
